import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, Loader2 } from 'lucide-react';
import { saveTransitionLead, markTransitionMeetingBooked } from './transitionLead';

/* Google Calendar appointment schedule (2026-10-13; replaces the LeadConnector "RentDirect Onboarding
   Call" widget). `gv=true` is Google's embed form: the plain page refuses to load inside an iframe. */
const SCHEDULE_URL =
  'https://calendar.google.com/calendar/appointments/schedules/AcZssZ37_QdQyCkrnQldLi5JSq5Ff8ZGsm9LZsEqUdTJXZUyGJy35Qe7k9tV5-pCFzECFTD3Ih72gUgi?gv=true';
/** Where visitors land after they finish booking and close this page. */
const RETURN_URL = '/turo-alternative';

const STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','District of Columbia',
  'Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland',
  'Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire',
  'New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania',
  'Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington',
  'West Virginia','Wisconsin','Wyoming',
];

const EMPTY = {
  firstName: '',
  lastName: '',
  businessName: '',
  email: '',
  phone: '',
  state: '',
  fleetSize: '',
  turoVehicleCount: '',
  vehicleTypes: [],
  turoStatus: '',
  transitionReasons: [],
  desiredRentalModel: '',
  transitionTimeline: '',
  offRentInsurance: [],
  offRentInterest: '',
  onRentInterest: '',
  rentalTypes: [],
  roadsideInterest: '',
  existingWebsite: '',
  currentlyDirect: '',
  rentalManagement: '',
  rentalAgreement: '',
  renterVerification: '',
  needsHelpWith: [],
};

const VEHICLE_TYPES = ['Economy', 'Standard', 'Luxury', 'Exotic', 'SUV', 'Trucks', 'Other'];
const TURO_STATUS = ['Active', 'Restricted', 'Suspended', 'Banned', 'Leaving voluntarily', 'No longer using Turo'];
const TRANSITION_REASONS = [
  'Build my own rental brand',
  'More control over my business',
  'Increase profitability',
  'Build direct renter relationships',
  'Turo fees',
  'Insurance',
  'Account restricted or banned',
  'Concerned about marketplace dependency',
  'Scaling my fleet',
  'Other',
];
const TIMELINES = ['Immediately', 'Within 30 days', '1–3 months', '3–6 months', 'Just exploring'];
const OFF_RENT_INSURERS = ['INSHUR', 'ABI', 'Roamly', 'Tint', 'Commercial Auto', 'Personal Auto', 'Other', "I'm not sure"];
const YES_NO_UNSURE = ['Yes', 'No', 'Not sure'];
const RENTAL_TYPES = ['Private / Direct', 'Leisure', 'Rideshare / Gig', 'Long-term', 'Delivery', 'Combination'];
const ROADSIDE = ['Yes', 'No', 'Tell me more'];
const YES_NO = ['Yes', 'No'];
const MANAGEMENT = ['Turo only', 'Rental management software', 'Spreadsheets', 'Manually', 'Another platform', 'Not yet'];
const VERIFICATION = ['Turo', 'Manually', 'Verification provider', 'Rental software', "I don't currently verify renters"];
const HELP_AREAS = [
  'Direct booking website',
  'Fleet management',
  'Off Rent insurance',
  'On Rent insurance',
  'Payments',
  'Security deposits',
  'Rental agreements',
  'Renter verification',
  'Roadside assistance',
  'AI & automation',
  'Operations & logistics',
  'Post-trip workflows',
  'Marketing & growth',
  'Going fully direct',
  'Everything',
];

const STEP_META = [
  { eyebrow: 'Your fleet', title: 'Tell us about your rental business.', sub: "We'll use this to understand the size and structure of the operation you're transitioning." },
  { eyebrow: 'Your marketplace business', title: 'Where are you today?', sub: "Help us understand what's driving your move toward private and direct rentals." },
  { eyebrow: 'Protect your fleet', title: 'Tell us about your insurance.', sub: 'Insurance is one of the most important parts of transitioning to independent rentals.' },
  { eyebrow: 'Your direct operation', title: 'What do you already have in place?', sub: "We'll identify what you can keep, what you may need, and where Rentid can help." },
];

/* ---------- field primitives ---------- */

const FieldLabel = ({ children, required }) => (
  <span className="mpa-field-label">
    {children}
    {required && (
      <span className="mpa-field-req" aria-hidden="true">
        *
      </span>
    )}
  </span>
);

const TextField = ({ label, value, onChange, type = 'text', required, placeholder, inputMode }) => (
  <label className="mpa-field">
    <FieldLabel required={required}>{label}</FieldLabel>
    <input
      type={type}
      inputMode={inputMode}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="mpa-input"
    />
  </label>
);

const SelectField = ({ label, value, onChange, options, required }) => (
  <label className="mpa-field">
    <FieldLabel required={required}>{label}</FieldLabel>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="mpa-input mpa-select">
      <option value="">Select a state</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  </label>
);

const ChoicePill = ({ option, selected, onClick }) => (
  <button
    type="button"
    aria-pressed={selected}
    onClick={onClick}
    className={`mpa-choice${selected ? ' is-selected' : ''}`}
  >
    {option}
    {selected && <Check size={14} strokeWidth={3} />}
  </button>
);

const ChoiceGroup = ({ label, options, value, onChange, required, columns = 'auto' }) => (
  <fieldset className="mpa-fieldset">
    <legend className="mpa-legend">
      <FieldLabel required={required}>{label}</FieldLabel>
    </legend>
    <div className={columns === 'grid' ? 'mpa-choice-grid' : 'mpa-choice-row'}>
      {options.map((option) => (
        <ChoicePill key={option} option={option} selected={value === option} onClick={() => onChange(option)} />
      ))}
    </div>
  </fieldset>
);

const MultiGroup = ({ label, options, values, onToggle, required }) => (
  <fieldset className="mpa-fieldset">
    <legend className="mpa-legend">
      <FieldLabel required={required}>{label}</FieldLabel>
      <span className="mpa-field-hint">Select all that apply</span>
    </legend>
    <div className="mpa-choice-row">
      {options.map((option) => (
        <ChoicePill
          key={option}
          option={option}
          selected={values.includes(option)}
          onClick={() => onToggle(option)}
        />
      ))}
    </div>
  </fieldset>
);

/* ---------- the assessment ---------- */

const TransitionAssessment = () => {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState(EMPTY);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  /* The Google booking page runs inside a cross-origin iframe and does not post a completion
     message up to us, so there is no automatic confirmation; the manual return link below the
     calendar covers it. */
  const booked = false;
  const [attribution, setAttribution] = useState({});

  const headingRef = useRef(null);
  const rootRef = useRef(null);
  const successRef = useRef(null);
  const started = useRef(false);

  // Preserve campaign attribution from the landing URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const captured = {};
    params.forEach((value, key) => {
      if (key.startsWith('utm_') || key === 'gclid' || key === 'fbclid' || key === 'ref') captured[key] = value;
    });
    setAttribution(captured);
  }, []);

  // Take focus on the Step 1 heading as the form appears.
  useEffect(() => {
    if (started.current) return undefined;
    started.current = true;
    const timer = window.setTimeout(() => headingRef.current?.focus(), 450);
    return () => window.clearTimeout(timer);
  }, []);

  const set = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    setError(null);
  };

  const toggle = (key, value) => {
    setAnswers((prev) => {
      const current = prev[key];
      return { ...prev, [key]: current.includes(value) ? current.filter((v) => v !== value) : [...current, value] };
    });
    setError(null);
  };

  const validate = (current) => {
    if (current === 1) {
      const missing =
        !answers.firstName.trim() ||
        !answers.lastName.trim() ||
        !answers.businessName.trim() ||
        !answers.email.trim() ||
        !answers.phone.trim() ||
        !answers.state ||
        !answers.fleetSize.trim();
      if (missing) return 'Please complete the required fields before continuing.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(answers.email.trim())) return 'Please enter a valid email address.';
    }
    if (current === 2) {
      if (!answers.turoStatus || answers.transitionReasons.length === 0 || !answers.transitionTimeline)
        return 'Please answer each question before continuing.';
    }
    if (current === 3) {
      if (answers.offRentInsurance.length === 0 || !answers.onRentInterest)
        return 'Please answer the required insurance questions.';
    }
    if (current === 4) {
      if (answers.needsHelpWith.length === 0) return 'Tell us what you need the most help with.';
    }
    return null;
  };

  const goNext = () => {
    const problem = validate(step);
    if (problem) {
      setError(problem);
      return;
    }
    setStep((s) => Math.min(4, s + 1));
    rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const goBack = () => {
    setError(null);
    setStep((s) => Math.max(1, s - 1));
    rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const submit = async () => {
    const problem = validate(4);
    if (problem) {
      setError(problem);
      return;
    }
    setSubmitting(true);
    try {
      await saveTransitionLead({ ...answers, attribution });
      setDone(true);
      rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch {
      setError('Something went wrong saving your assessment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const chips = useMemo(() => {
    const list = [];
    if (answers.fleetSize) list.push(`${answers.fleetSize} ${answers.fleetSize === '1' ? 'Vehicle' : 'Vehicles'}`);
    if (answers.state) list.push(answers.state);
    if (answers.turoStatus) list.push(`${answers.turoStatus} Turo Host`);
    if (answers.desiredRentalModel) list.push(answers.desiredRentalModel);
    if (answers.onRentInterest === 'Yes') list.push('Needs On Rent Coverage');
    if (answers.offRentInterest === 'Yes') list.push('Needs Off Rent Coverage');
    if (answers.roadsideInterest === 'Yes') list.push('Wants Roadside Assistance');
    if (answers.transitionTimeline) list.push(`Launch ${answers.transitionTimeline}`);
    return list;
  }, [answers]);

  // Google's appointment page takes no prefill parameters; the visitor's details already went to GHL.
  const calendarUrl = SCHEDULE_URL;

  const openCalendar = () => {
    setShowCalendar(true);
    markTransitionMeetingBooked(answers.email);
  };

  // Move focus to the confirmation so it's announced and immediately visible.
  useEffect(() => {
    if (!booked) return undefined;
    const timer = window.setTimeout(() => {
      successRef.current?.focus();
      successRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [booked]);

  /* "Close this page" can only truly close a tab the site opened itself. Every
     CTA into this flow is a same-tab link, so close() will usually be blocked —
     fall back to sending them to the top of the page they came from. */
  const closeAndReturn = () => {
    if (window.opener && !window.opener.closed) {
      window.opener.focus?.();
      window.close();
    }
    window.setTimeout(() => {
      window.location.href = RETURN_URL;
    }, 150);
  };

  /* ---------- completion screen ---------- */

  if (done) {
    return (
      <div ref={rootRef} className="mpa-done">
        <p className="mpa-eyebrow mpa-eyebrow--tight">Your transition profile</p>
        <h3 ref={headingRef} tabIndex={-1} className="mpa-done-title">
          Your transition profile is ready.
        </h3>

        <ul className="mpa-chips">
          {chips.map((chip) => (
            <li key={chip} className="mpa-chip">
              <Check size={14} strokeWidth={3} />
              {chip}
            </li>
          ))}
        </ul>

        <div className="mpa-done-next">
          <h4 className="mpa-done-subtitle">Let&apos;s build the plan together.</h4>
          <p className="mpa-done-copy">
            Your Rentid expert will use this information to prepare for your transition strategy
            meeting so the conversation can focus on your business, your gaps, and your path forward.
          </p>

          {!showCalendar ? (
            <div className="mpa-done-ctas">
              <button type="button" onClick={openCalendar} className="mpa-form-next">
                Choose a Time
              </button>
            </div>
          ) : (
            <div className="mpa-calendar">
              {booked && (
                <div ref={successRef} tabIndex={-1} role="status" aria-live="polite" className="mpa-booked">
                  <span className="mpa-booked-check">
                    <Check size={24} strokeWidth={3} />
                  </span>
                  <div className="mpa-booked-body">
                    <h5 className="mpa-booked-title">You&apos;re all set &mdash; your meeting is booked.</h5>
                    <p className="mpa-booked-copy">
                      A calendar invite with your meeting link is on its way to{' '}
                      {answers.email ? <span className="mpa-booked-email">{answers.email}</span> : 'your inbox'}.
                      Nothing else is needed here, so you can close this page &mdash; we&apos;ll take
                      you back to where you left off.
                    </p>
                    <button type="button" onClick={closeAndReturn} className="mpa-form-next mpa-booked-btn">
                      Close &amp; Return to Rentid
                    </button>
                  </div>
                </div>
              )}

              <div className="mpa-calendar-frame">
                {/* The booking widget grows once details are entered, so give it room and let it scroll. */}
                <iframe
                  src={calendarUrl}
                  id="rentid-transition-strategy-call"
                  title="Schedule your transition strategy meeting"
                  scrolling="yes"
                />
              </div>

              {/* Confirmation detection depends on the widget posting up to us, so
                  always leave a manual way back rather than trapping anyone here. */}
              {!booked && (
                <p className="mpa-calendar-fallback">
                  Finished booking?{' '}
                  <button type="button" onClick={closeAndReturn} className="mpa-calendar-return">
                    Close this page and return to Rentid
                  </button>
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ---------- multi-step form ---------- */

  const meta = STEP_META[step - 1];

  return (
    <div ref={rootRef} className="mpa-form">
      {/* Progress rail, seated flush against the top edge of the card. */}
      <div
        className="mpa-form-rail"
        role="progressbar"
        aria-valuenow={step}
        aria-valuemin={1}
        aria-valuemax={4}
        aria-label={`Step ${step} of 4`}
      >
        <div className="mpa-form-rail-fill" style={{ width: `${(step / 4) * 100}%` }} />
      </div>

      <div className="mpa-form-body">
        <div key={step} className="mpa-form-step">
          <p className="mpa-eyebrow mpa-eyebrow--tight">{meta.eyebrow}</p>
          <h3 ref={headingRef} tabIndex={-1} className="mpa-form-title">
            {meta.title}
          </h3>
          <p className="mpa-form-sub">{meta.sub}</p>

          <div className="mpa-form-fields">
            {step === 1 && (
              <>
                <div className="mpa-field-grid">
                  <TextField label="First name" value={answers.firstName} onChange={(v) => set('firstName', v)} required />
                  <TextField label="Last name" value={answers.lastName} onChange={(v) => set('lastName', v)} required />
                </div>
                <TextField label="Business name" value={answers.businessName} onChange={(v) => set('businessName', v)} required />
                <div className="mpa-field-grid">
                  <TextField label="Email" type="email" inputMode="email" value={answers.email} onChange={(v) => set('email', v)} required />
                  <TextField label="Phone number" type="tel" inputMode="tel" value={answers.phone} onChange={(v) => set('phone', v)} required />
                </div>
                <SelectField
                  label="What state is your fleet primarily located in?"
                  value={answers.state}
                  onChange={(v) => set('state', v)}
                  options={STATES}
                  required
                />
                <TextField
                  label="How many vehicles are in your fleet?"
                  type="number"
                  inputMode="numeric"
                  placeholder="0"
                  value={answers.fleetSize}
                  onChange={(v) => set('fleetSize', v)}
                  required
                />
                <TextField
                  label="How many vehicles are currently listed on Turo?"
                  type="number"
                  inputMode="numeric"
                  placeholder="0"
                  value={answers.turoVehicleCount}
                  onChange={(v) => set('turoVehicleCount', v)}
                />
                <MultiGroup
                  label="What types of vehicles do you rent?"
                  options={VEHICLE_TYPES}
                  values={answers.vehicleTypes}
                  onToggle={(v) => toggle('vehicleTypes', v)}
                />
              </>
            )}

            {step === 2 && (
              <>
                <ChoiceGroup
                  label="What is your current Turo status?"
                  options={TURO_STATUS}
                  value={answers.turoStatus}
                  onChange={(v) => set('turoStatus', v)}
                  required
                />
                <MultiGroup
                  label="What's driving your transition from Turo?"
                  options={TRANSITION_REASONS}
                  values={answers.transitionReasons}
                  onToggle={(v) => toggle('transitionReasons', v)}
                  required
                />
                <ChoiceGroup
                  label="When would you like to start accepting direct rentals?"
                  options={TIMELINES}
                  value={answers.transitionTimeline}
                  onChange={(v) => set('transitionTimeline', v)}
                  required
                />
              </>
            )}

            {step === 3 && (
              <>
                <MultiGroup
                  label="How are your vehicles currently insured when they're Off Rent?"
                  options={OFF_RENT_INSURERS}
                  values={answers.offRentInsurance}
                  onToggle={(v) => toggle('offRentInsurance', v)}
                  required
                />
                <ChoiceGroup
                  label="Are you looking to change or lower your insurance payments?"
                  options={YES_NO_UNSURE}
                  value={answers.offRentInterest}
                  onChange={(v) => set('offRentInterest', v)}
                />
                <ChoiceGroup
                  label="Are you looking for On Rent coverage for your direct rentals?"
                  options={YES_NO_UNSURE}
                  value={answers.onRentInterest}
                  onChange={(v) => set('onRentInterest', v)}
                  required
                />
                <MultiGroup
                  label="What types of rentals do you plan to offer?"
                  options={RENTAL_TYPES}
                  values={answers.rentalTypes}
                  onToggle={(v) => toggle('rentalTypes', v)}
                />
                <ChoiceGroup
                  label="Are you interested in Roadside Assistance?"
                  options={ROADSIDE}
                  value={answers.roadsideInterest}
                  onChange={(v) => set('roadsideInterest', v)}
                />
              </>
            )}

            {step === 4 && (
              <>
                <ChoiceGroup
                  label="Do you already have a rental website?"
                  options={YES_NO}
                  value={answers.existingWebsite}
                  onChange={(v) => set('existingWebsite', v)}
                />
                <ChoiceGroup
                  label="Are you currently accepting private or direct rentals?"
                  options={YES_NO}
                  value={answers.currentlyDirect}
                  onChange={(v) => set('currentlyDirect', v)}
                />
                <ChoiceGroup
                  label="How do you currently manage your rentals?"
                  options={MANAGEMENT}
                  value={answers.rentalManagement}
                  onChange={(v) => set('rentalManagement', v)}
                />
                <ChoiceGroup
                  label="Do you currently have your own rental agreement?"
                  options={YES_NO}
                  value={answers.rentalAgreement}
                  onChange={(v) => set('rentalAgreement', v)}
                />
                <ChoiceGroup
                  label="How are you currently verifying renters?"
                  options={VERIFICATION}
                  value={answers.renterVerification}
                  onChange={(v) => set('renterVerification', v)}
                />
                <MultiGroup
                  label="What do you need the most help with?"
                  options={HELP_AREAS}
                  values={answers.needsHelpWith}
                  onToggle={(v) => toggle('needsHelpWith', v)}
                  required
                />
              </>
            )}
          </div>
        </div>

        {error && (
          <p role="alert" className="mpa-form-error">
            {error}
          </p>
        )}

        {/* Navigation */}
        <div className="mpa-form-nav">
          {step > 1 && (
            <button type="button" onClick={goBack} className="mpa-form-back">
              <ArrowLeft size={16} />
              Back
            </button>
          )}

          {step < 4 ? (
            <button type="button" onClick={goNext} className="mpa-form-next">
              Continue
              <ArrowRight size={16} />
            </button>
          ) : (
            <button type="button" onClick={submit} disabled={submitting} className="mpa-form-next">
              {submitting && <Loader2 size={16} className="mpa-spin" />}
              Continue &amp; Schedule Meeting
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransitionAssessment;
